import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { retrieveCheckoutSession } from '../utils/products';
import { formatPrice } from '../utils/formatters';

interface SessionData {
  session: {
    id: string;
    payment_status: string;
    status: string;
    amount_total: number;
    currency: string;
    customer_email: string;
    shipping: any;
    metadata: any;
    created: number;
  };
  line_items: Array<{
    description: string;
    quantity: number;
    amount_total: number;
    currency: string;
  }>;
}

const CheckoutSuccessPage: React.FC = () => {
  const location = useLocation();
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get('session_id');

    if (sessionId) {
      loadSessionData(sessionId);
    } else {
      setIsLoading(false);
    }
  }, [location]);

  const loadSessionData = async (sessionId: string) => {
    try {
      setIsLoading(true);
      const data = await retrieveCheckoutSession(sessionId);
      setSessionData(data);
    } catch (err) {
      console.error('Error loading session data:', err);
      setError('Failed to load order details');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="pt-16 min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-12 rounded-xl shadow-xl max-w-md">
          <FontAwesomeIcon 
            icon={faSpinner} 
            className="text-[#2b603e] text-6xl mb-6 mx-auto animate-spin" 
          />
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            Loading Order Details...
          </h1>
          <p className="text-gray-600">
            Please wait while we retrieve your order information.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-16 min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-12 rounded-xl shadow-xl max-w-md">
          <FontAwesomeIcon 
            icon={faCheckCircle} 
            className="text-green-600 text-6xl mb-6 mx-auto" 
          />
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            Thank You for Your Purchase!
          </h1>
          <p className="text-gray-600 mb-6">
            Your order has been successfully processed. We appreciate your business!
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/products" 
              className="bg-[#2b603e] text-white px-6 py-3 rounded-full hover:bg-[#1a3d25] transition-colors"
            >
              Continue Shopping
            </Link>
            <Link 
              to="/" 
              className="bg-gray-200 text-gray-800 px-6 py-3 rounded-full hover:bg-gray-300 transition-colors"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Success Header */}
          <div className="text-center bg-white p-8 rounded-xl shadow-lg mb-8">
            <FontAwesomeIcon 
              icon={faCheckCircle} 
              className="text-green-600 text-6xl mb-6 mx-auto" 
            />
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              Thank You for Your Purchase!
            </h1>
            <p className="text-gray-600 text-lg">
              Your order has been successfully processed. We appreciate your business!
            </p>
          </div>

          {/* Order Details */}
          {sessionData && (
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Order Details</h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* Order Summary */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Order Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Order ID:</span>
                      <span className="font-mono text-sm">{sessionData.session.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className="capitalize text-green-600 font-semibold">
                        {sessionData.session.payment_status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total:</span>
                      <span className="font-bold text-lg">
                        {formatPrice(sessionData.session.amount_total / 100)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span>{sessionData.session.customer_email || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Items Ordered</h3>
                  <div className="space-y-3">
                    {sessionData.line_items.map((item, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                        <div>
                          <p className="font-medium text-gray-800">{item.description}</p>
                          <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                        </div>
                        <span className="font-semibold">
                          {formatPrice(item.amount_total / 100)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Shipping Information */}
              {sessionData.session.shipping && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Shipping Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-600">Name:</p>
                      <p className="font-medium">
                        {sessionData.session.shipping.name || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Address:</p>
                      <p className="font-medium">
                        {sessionData.session.shipping.address?.line1 || 'N/A'}
                        {sessionData.session.shipping.address?.line2 && 
                          `, ${sessionData.session.shipping.address.line2}`
                        }
                      </p>
                      <p className="text-sm text-gray-600">
                        {sessionData.session.shipping.address?.city || ''}
                        {sessionData.session.shipping.address?.postal_code && 
                          `, ${sessionData.session.shipping.address.postal_code}`
                        }
                        {sessionData.session.shipping.address?.country && 
                          `, ${sessionData.session.shipping.address.country}`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="text-center">
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link 
                to="/products" 
                className="bg-[#2b603e] text-white px-8 py-3 rounded-full hover:bg-[#1a3d25] transition-colors font-semibold"
              >
                Continue Shopping
              </Link>
              <Link 
                to="/" 
                className="bg-gray-200 text-gray-800 px-8 py-3 rounded-full hover:bg-gray-300 transition-colors font-semibold"
              >
                Return to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage; 